import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Image, Upload } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { getImageUrl } from '../../utils/imageUrl';
import { Card, CardContent } from '../ui/card';

interface IPredefinedAvatar {
  id: string;
  url: string;
  name?: string;
  createdAt: string;
}

const AvatarManagementSection = () => {
  const { t } = useTranslation();
  const [avatars, setAvatars] = useState<IPredefinedAvatar[]>([]);

  const load = async () => {
    try {
      setAvatars(await api.get<IPredefinedAvatar[]>('/admin/avatars'));
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm(t('config.confirm_delete_avatar'))) return;

    try {
      await api.delete(`/admin/avatars/${id}`);
      setAvatars((prev) => prev.filter((a) => a.id !== id));
    } catch (err: any) {
      toast.error(err.message || t('common.operation_failed'));
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      try {
        await api.upload('/admin/avatars/upload', file);
      } catch (err: any) {
        toast.error(err.message || t('common.upload_failed'));
      }
    }

    load();
    e.target.value = '';
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-2">
          <Image className="w-5 h-5 text-foreground" />
          <h3 className="text-base font-medium text-foreground">
            {t('config.avatar_management')}
          </h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {t('config.no_avatars_hint')}
        </p>

        {avatars.length > 0 && (
          <div className="grid grid-cols-6 gap-4 mb-4">
            {avatars.map((avatar) => (
              <div key={avatar.id} className="relative group">
                <img
                  src={getImageUrl(avatar.url)}
                  alt=""
                  className="w-full aspect-square rounded-lg border-2 border-gray-200 object-cover"
                />
                <button
                  onClick={() => handleDelete(avatar.id)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <span className="block w-3 h-3 leading-3 text-xs">✕</span>
                </button>
              </div>
            ))}
          </div>
        )}

        <label className="cursor-pointer block">
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleUpload}
          />
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600">
              {t('config.upload_avatar_hint')}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('config.upload_avatar_format')}
            </p>
          </div>
        </label>
      </CardContent>
    </Card>
  );
};

export default AvatarManagementSection;
